import { Alert, Pressable, Text, View } from 'react-native';
import { CartaoApoio } from '../components/Recolhivel';
import type { Reg } from '../store/types';
import type { Colecao, ModDef, PainelProps } from './tipos';
import { dataBr, din, junta, num, op, soma, temNum } from './util';
import { novoReg, salvarVarios } from '../store/modulos';
import { C } from '../components/theme';
import { paisAparelho } from '../lib/moeda';
import { today } from '../lib/date';
import { N_, t } from '../lib/i18n';

const TIPOS_BEM = [N_('Imóvel'), N_('Veículo'), N_('Ações'), N_('Fundos e renda fixa'), N_('Criptomoedas'), N_('Participação em empresa'), N_('Outro')];
const ICONE_BEM: Record<string, string> = { 'Imóvel': '🏠', 'Veículo': '🚗', 'Ações': '📈', 'Fundos e renda fixa': '🏦', 'Criptomoedas': '🪙', 'Participação em empresa': '🏢' };
const TIPOS_RECIBO = [N_('Saúde (médicos, dentistas, exames, plano)'), N_('Educação'), N_('Previdência privada'), N_('Pensão alimentícia'), N_('Doações'), N_('Dependentes'), N_('Outro')];

const ganho = (r: Reg) => (temNum(r.c.venda) ? num(r.c.venda) - num(r.c.compra) : null);
const anoAtual = () => Number(today().slice(0, 4));

/** Totais do ano: recibos dedutíveis por tipo e ganho nas vendas. */
function PainelAno({ ctx }: PainelProps) {
  const ano = String(anoAtual());
  const recibos = ctx.regs.filter(r => r.col === 'recibo' && String(r.c.data || '').startsWith(ano));
  const vendas = ctx.regs.filter(r => r.col === 'bem' && String(r.c.dataVenda || '').startsWith(ano));
  if (!recibos.length && !vendas.length) return null;
  const por = TIPOS_RECIBO.map(tp => [tp, soma(recibos.filter(r => r.c.tipo === tp), 'valor')] as const).filter(([, v]) => v > 0);
  const lucro = vendas.reduce((s, r) => s + (ganho(r) ?? 0), 0);
  return (
    <CartaoApoio titulo={'🧾 ' + t('Ano de {ano}', { ano })} chave={'patr:ano'} resumo={recibos.length ? din(soma(recibos, 'valor'), ctx.moeda) : ''} gap={4}>
      {por.map(([tp, v]) => <Text key={tp} style={{ color: C.sub, fontSize: 13 }}>{t(tp)}: <Text style={{ color: C.text }}>{din(v, ctx.moeda)}</Text></Text>)}
      {!!recibos.length && <Text style={{ color: C.text, fontSize: 13, fontWeight: '700' }}>{t('Total de recibos: {v}', { v: din(soma(recibos, 'valor'), ctx.moeda) })}</Text>}
      {!!vendas.length && <Text style={{ color: lucro >= 0 ? C.ok : C.danger, fontSize: 13, fontWeight: '700' }}>{t('Resultado nas vendas: {v}', { v: din(lucro, ctx.moeda) })}</Text>}
      <Text style={{ color: C.mute, fontSize: 11 }}>{t('Valores para conferir com o seu contador. As regras de dedução mudam a cada ano.')}</Text>
    </CartaoApoio>
  );
}

/** Prazos do Imposto de Renda do Brasil (datas de referência: confira no site da Receita Federal a cada ano). */
function PainelPrazos({ ctx, lista }: PainelProps) {
  if (lista.length || paisAparelho() !== 'BR') return null;
  const a = anoAtual() + (today().slice(5) > '05-31' ? 1 : 0);
  function criar() {
    salvarVarios(ctx.mod, [
      novoReg('prazo', null, { nome: t('Separar informes de rendimentos (bancos, empregador, corretoras)'), data: `${a}-02-28` }),
      novoReg('prazo', null, { nome: t('Abertura do prazo da declaração do IR'), data: `${a}-03-16` }),
      novoReg('prazo', null, { nome: t('Último dia para entregar a declaração do IR'), data: `${a}-05-29` }),
      novoReg('prazo', null, { nome: t('Primeira cota ou cota única do IR'), data: `${a}-05-29` }),
    ]);
    Alert.alert(t('Prazos criados'), t('Confira as datas oficiais no site da Receita Federal e ajuste se precisar.'));
  }
  return (
    <Pressable onPress={criar} style={{ backgroundColor: ctx.cor, borderRadius: 12, padding: 12, alignItems: 'center' }}>
      <Text style={{ color: '#fff', fontWeight: '800' }}>🇧🇷 {t('Criar os prazos do IR de {ano}', { ano: a })}</Text>
    </Pressable>
  );
}

const bem: Colecao = {
  id: 'bem', nome: N_('Bens'), um: N_('Bem'), icone: r => ICONE_BEM[r.c.tipo] ?? '💼',
  campos: [
    { k: 'tipo', r: N_('Tipo'), t: 'opcao', op: TIPOS_BEM },
    { k: 'desc', r: N_('Descrição'), t: 'texto', ph: N_('Ex.: Apartamento na Rua X, 100 ações PETR4') },
    { k: 'endereco', r: N_('Endereço do imóvel'), t: 'endereco', mostrar: c => c.tipo === 'Imóvel' },
    { k: 'doc', r: N_('Matrícula, placa, código ou CNPJ'), t: 'cofre' },
    { k: 'qtd', r: N_('Quantidade'), t: 'numero', mostrar: c => c.tipo === 'Ações' || c.tipo === 'Criptomoedas' || c.tipo === 'Fundos e renda fixa' },
    { k: 'dataCompra', r: N_('Data da compra'), t: 'data' },
    { k: 'compra', r: N_('Valor pago'), t: 'dinheiro' },
    { k: 'dataVenda', r: N_('Data da venda'), t: 'data' },
    { k: 'venda', r: N_('Valor da venda'), t: 'dinheiro' },
    { k: 'notas', r: N_('Observações'), t: 'longo' },
  ],
  titulo: r => r.c.desc || op(r.c.tipo),
  sub: (r, ctx) => { const g = ganho(r); return junta(op(r.c.tipo), r.c.dataCompra && t('comprado em {d} por {v}', { d: dataBr(r.c.dataCompra), v: din(r.c.compra, ctx.moeda) }),
    g != null && (g >= 0 ? t('vendido com ganho de {v}', { v: din(g, ctx.moeda) }) : t('vendido com perda de {v}', { v: din(-g, ctx.moeda) }))); },
  ordem: (a, b) => String(b.c.dataCompra || '').localeCompare(String(a.c.dataCompra || '')),
  feito: r => !!r.c.dataVenda, anexos: true,
  filtros: [[N_('Atuais'), r => !r.c.dataVenda], [N_('Vendidos'), r => !!r.c.dataVenda]],
  resumo: (l, ctx) => { const atuais = l.filter(r => !r.c.dataVenda); return atuais.length ? t('Patrimônio pelo valor de compra: {v}', { v: din(soma(atuais, 'compra'), ctx.moeda) }) : null; },
  vazio: N_('Registre compras e vendas de imóveis, veículos e investimentos com os comprovantes. Ajuda muito na hora do imposto.'),
};

const recibo: Colecao = {
  id: 'recibo', nome: N_('Recibos dedutíveis'), um: N_('Recibo'), icone: r => (String(r.c.tipo).startsWith('Saúde') ? '🩺' : r.c.tipo === 'Educação' ? '🎒' : '🧾'),
  campos: [
    { k: 'tipo', r: N_('Tipo'), t: 'opcao', op: TIPOS_RECIBO },
    { k: 'prestador', r: N_('Quem recebeu (médico, escola...)'), t: 'texto' },
    { k: 'docPrestador', r: N_('CPF ou CNPJ de quem recebeu'), t: 'texto' },
    { k: 'quem', r: N_('Paciente ou aluno'), t: 'texto' },
    { k: 'data', r: N_('Data do pagamento'), t: 'data' },
    { k: 'valor', r: N_('Valor'), t: 'dinheiro' },
  ],
  titulo: r => r.c.prestador || op(r.c.tipo), sub: (r, ctx) => junta(dataBr(r.c.data), din(r.c.valor, ctx.moeda), r.c.quem),
  ordem: 'data-desc', anexos: true, painel: PainelAno,
  novo: () => ({ data: today() }),
  filtros: [[N_('Este ano'), r => String(r.c.data || '').startsWith(String(anoAtual()))], [N_('Ano passado'), r => String(r.c.data || '').startsWith(String(anoAtual() - 1))]],
  vazio: N_('Fotografe cada recibo de médico, dentista, escola e previdência assim que pagar. No fim do ano está tudo pronto.'),
};

const prazo: Colecao = {
  id: 'prazo', check: 'feito', nome: N_('Calendário do imposto'), um: N_('Prazo'), icone: r => (r.c.feito ? '✅' : '📆'),
  campos: [
    { k: 'nome', r: N_('O que fazer'), t: 'texto' },
    { k: 'data', r: N_('Data'), t: 'data', aviso: [15, 3, 0] },
    { k: 'feito', r: N_('Feito'), t: 'sim' },
    { k: 'notas', r: N_('Observações (recibo de entrega, número...)'), t: 'longo' },
  ],
  titulo: r => r.c.nome || '', data: 'data', feito: r => !!r.c.feito, anexos: true, painel: PainelPrazos,
  agenda: () => ({}),
};

export const PATRIMONIO: ModDef = { id: 'patrimonio', colecoes: [bem, recibo, prazo] };
